"use client"
import type { Post } from '@/lib/api'
import Link from 'next/link'
import Image from 'next/image'
import { useLocale, useTranslations } from 'next-intl'
import { formatVideoDuration } from '@/lib/constants'

export default function LatestVideos({ videos }: { videos: Post[] }) {
  const t = useTranslations('ui')
  const locale = useLocale()
  
  if (!videos || !videos.length) return null
  
  return (
    <aside className="overflow-hidden rounded-xl bg-white shadow-sm">
      {/* Header avec gradient rouge pour vidéos */}
      <div className="border-b bg-gradient-to-r from-red-600 to-red-700 p-4">
        <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-white">
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
          </svg>
          {t('latestVideos')}
        </h3>
      </div>
      
      {/* Liste de vidéos avec thumbnails */}
      <div className="p-4">
        <div className="space-y-4">
          {videos.map((video) => (
            <Link
              key={video.id}
              prefetch
              href={`/${locale}/${video.category}/${video.slug}`}
              className="group flex gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors duration-200"
            >
              {/* Thumbnail avec durée */}
              <div className="relative h-20 w-32 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                <Image
                  src={video.coverImage}
                  alt={video.title}
                  fill
                  className="object-cover transition-transform duration-300 group-hover:scale-110"
                  sizes="128px"
                />
                
                {/* Overlay play button */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 transition-opacity group-hover:opacity-100">
                  <div className="rounded-full bg-red-600 p-2">
                    <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                    </svg>
                  </div>
                </div>
                
                {/* Badge durée */}
                <span className="absolute bottom-1 right-1 rounded bg-black/80 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                  {formatVideoDuration()}
                </span>
              </div>
              
              {/* Info vidéo */}
              <div className="flex-1 min-w-0">
                <h4 className="line-clamp-3 text-sm font-semibold leading-[1.4] text-gray-900 transition-colors group-hover:text-red-600 mb-1">
                  {video.title}
                </h4>
                
                <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">

                  <time>{new Date(video.publishedAt).toLocaleDateString(locale, {
                    day: 'numeric',
                    month: 'short'
                  })}</time>
                </div>
              </div>
            </Link>
          ))}
        </div>
        
        {/* Bouton voir toutes les vidéos */}
        <Link
          href={`/${locale}/videos`}
          className="mt-4 flex items-center justify-center gap-2 rounded-lg bg-red-50 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-100"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {locale === 'es' ? 'Ver todos los vídeos' : 'Voir toutes les vidéos'}
        </Link>
      </div>
    </aside>
  )
}

